// Sticky product navigation bar — reveals after scrolling past the main product,
// smooth-scrolls to in-page sections, and highlights the section in view.
// Self-contained custom element (multiple instances / Theme Editor safe).
if (!customElements.get('product-sticky-bar')) {
  customElements.define(
    'product-sticky-bar',
    class ProductStickyBar extends HTMLElement {
      connectedCallback() {
        this.bar = this.querySelector('[data-psb-bar]');
        this.links = Array.from(this.querySelectorAll('[data-psb-target]'));
        this.resolveTargets();

        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.ticking = false;

        this.links.forEach((a) => a.addEventListener('click', (e) => this.scrollToTarget(e, a)));
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);

        this.measure();
        this.update();

        // Lazy-loaded media below can shift the anchor's position after first paint,
        // which would delay the reveal. Re-measure once the page (and late images) settle.
        window.addEventListener('load', () => { this.measure(); this.update(); });
        [400, 1200].forEach((t) => setTimeout(() => { this.measure(); this.update(); }, t));
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
      }

      measure() {
        this.barHeight = this.bar ? this.bar.offsetHeight : 56;
        this.headerOffset = this.computeHeaderOffset();
      }

      // Height of a sticky/fixed site header so the bar (and scroll target) clears it.
      computeHeaderOffset() {
        const header = document.querySelector(
          '.shopify-section.section-header, .shopify-section.header, header.header, .header-wrapper, [id$="__header_section"]'
        );
        if (header) {
          const h = Math.round(header.getBoundingClientRect().height);
          if (h > 0) return h;
        }
        // Fallback: the --header-height custom property this theme sets on <body>.
        const hv = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10);
        return isNaN(hv) ? 56 : hv;
      }

      resolveTargets() {
        this.targets = this.links.map((a) => {
          const t = (a.getAttribute('data-psb-target') || '').trim();
          let el = null;
          if (t) {
            if (t[0] === '#' || t[0] === '.') {
              try { el = document.querySelector(t); } catch (e) { el = null; }
            }
            // Match the section wrapper, whose id starts with "shopify-section" and
            // ends with the section key (e.g. shopify-section-template--123__faq).
            // Scoping to shopify-section avoids matching unrelated elements that merely
            // end with the key (e.g. a header menu link "HeaderDrawer-support-help-faq").
            if (!el) el = document.querySelector('[id^="shopify-section"][id$="' + t + '"]');
            if (!el) el = document.querySelector('[id$="' + t + '"]');
          }
          return { a, el };
        });
      }

      scrollToTarget(event, link) {
        const t = this.targets.find((x) => x.a === link);
        if (!t || !t.el) return; // unresolved → let the href behave normally
        event.preventDefault();
        this.measure();
        // Bar is fixed to the bottom, so only clear the sticky header at the top.
        const y = t.el.getBoundingClientRect().top + window.pageYOffset - (this.headerOffset + 12);
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.update();
          this.ticking = false;
        });
      }

      onResize() {
        this.measure();
        this.update();
      }

      update() {
        // Reveal as soon as the in-flow anchor (start of the description) reaches the
        // header area — a small margin makes the bar appear right as the Popis section
        // arrives, rather than only once it is flush under the header.
        const top = this.getBoundingClientRect().top;
        this.classList.toggle('psb--visible', top <= this.headerOffset + 64);

        // Highlight the link for the section currently below the sticky header.
        const probe = this.headerOffset + 24;
        let active = null;
        for (const t of this.targets) {
          if (t.el && t.el.getBoundingClientRect().top <= probe) active = t;
        }
        for (const t of this.targets) t.a.classList.toggle('is-active', t === active);
      }
    }
  );
}
